import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Divider,
  Input,
  InputNumber,
  Modal,
  message,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
} from "antd";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";

import { getRequest, postRequest } from "../../../axios";
import { ShowErrorMsg } from "../../../utils/Notifications";
import "../../../styles/Main.scss";

const { TextArea } = Input;

const mockContext = {
  sourceRef: "QUARANTINE-GRN-GOTA-001",
  itemName: "Basin Mixer Tap",
  returnQty: 5,
  uom: "pcs",
  reason: "QC Failed: Surface scratches observed during inspection.",
};

const DESTINATION_OPTIONS = [
  { label: "To Supplier (Replacement)", value: "To Supplier (Replacement)" },
  { label: "Scrap / Disposal", value: "Scrap / Disposal" },
  { label: "Return to Central Store", value: "Return to Central Store" },
];

const ReturnQuarantinedMaterial = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [context, setContext] = useState(location.state || mockContext);
  const [loading, setLoading] = useState(!!id);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      setContext(location.state || mockContext);
      return;
    }
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await getRequest(`quarantined-materials/${id}/`);
        const data = res?.data?.data ?? res?.data?.results ?? res?.data;
        if (data) {
          setContext({
            id: data.id,
            sourceRef: data.source_ref ?? data.sourceRef ?? "—",
            itemName: data.item_name ?? data.itemName ?? "—",
            returnQty: data.remaining_qty ?? data.remaining_qty ?? 0,
            uom: data.uom ?? "—",
            reason: data.rejection_reason ?? data.reason ?? "—",
            status: data.status,
            confirmation_date: data.confirmation_date,
            site_id: data.site_id,
            site_name: data.site_name,
            invoice_number: data.invoice_number,
            confirmed_by: data.confirmed_by,
          });
        }
      } catch (err) {
        ShowErrorMsg(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load quarantined material",
        );
        setContext(location.state || mockContext);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const [draft, setDraft] = useState([
    {
      key: "draft-1",
      itemName: context.itemName,
      returnQty: context.returnQty,
      destination: undefined,
      shipping_method: "",
      notes: "",
    },
  ]);

  useEffect(() => {
    if (!loading && context?.itemName) {
      setDraft((prev) =>
        prev.map((row, i) =>
          i === 0
            ? {
                ...row,
                itemName: context.itemName,
                returnQty: context.returnQty ?? row.returnQty,
              }
            : row,
        ),
      );
    }
  }, [loading, context?.itemName, context?.returnQty]);

  const infoRows = useMemo(
    () => [
      {
        key: "info-1",
        itemName: context.itemName,
        sourceRef: context.sourceRef,
        returnQty: context.returnQty,
        uom: context.uom,
        reason: context.reason,
      },
    ],
    [context],
  );

  const updateDraft = (key, field, value) => {
    setDraft((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  };

  const removeDraft = (key) => {
    setDraft((prev) => prev.filter((row) => row.key !== key));
  };

  const disableSubmit =
    draft.length === 0 ||
    draft.some((row) => !row.destination || !row.returnQty);

  const confirmSubmitReturnRequest = () => {
    Modal.confirm({
      title: "Submit Return Request?",
      content:
        "This will generate an RA ID. Do you want to continue?",
      okText: "Yes",
      cancelText: "No",
      onOk: handleSubmitReturnRequest,
    });
  };

  const handleSubmitReturnRequest = async () => {
    const row = draft[0];
    const quarantinedMaterialId = context?.id ?? id;
    if (!quarantinedMaterialId) {
      ShowErrorMsg(
        "Quarantined material ID is missing. Please open this page from the Quarantined Material Queue.",
      );
      return;
    }
    const payload = {
      quarantined_material_id: Number(quarantinedMaterialId),
      source_ref: context?.sourceRef ?? "",
      return_qty: row?.returnQty ?? 0,
      uom: context?.uom ?? "",
      intended_destination: row?.destination ?? "",
      shipping_method: row?.shipping_method?.trim() ?? "",
      notes: row?.notes?.trim() ?? "",
    };
    try {
      setSubmitLoading(true);
      await postRequest("return-requests/", payload);
      message.success("Return request submitted successfully.");
      navigate("/inventory-quarantined-material-queue");
    } catch (err) {
      ShowErrorMsg(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to submit return request",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const infoColumns = [
    { title: "Item Name", dataIndex: "itemName", key: "itemName" },
    { title: "Source Ref.", dataIndex: "sourceRef", key: "sourceRef" },
    {
      title: "Return Qty.",
      dataIndex: "returnQty",
      key: "returnQty",
      width: 120,
    },
    { title: "UoM.", dataIndex: "uom", key: "uom", width: 80 },
    { title: "Reason Context", dataIndex: "reason", key: "reason" },
  ];

  const draftColumns = [
    {
      title: "Item Name",
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: "Return Qty. (Confirmed)",
      dataIndex: "returnQty",
      key: "returnQty",
      width: 200,
      render: (_, record) => (
        <InputNumber
          min={0}
          value={record.returnQty}
          onChange={(val) => updateDraft(record.key, "returnQty", val)}
        />
      ),
    },
    {
      title: "Intended Destination",
      dataIndex: "destination",
      key: "destination",
      width: 240,
      render: (_, record) => (
        <Select
          allowClear
          placeholder="Select destination"
          value={record.destination}
          options={DESTINATION_OPTIONS}
          onChange={(val) => updateDraft(record.key, "destination", val)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Shipping Method",
      dataIndex: "shipping_method",
      key: "shipping_method",
      width: 220,
      render: (_, record) => (
        <Input
          placeholder="e.g. Courier / Transport"
          value={record.shipping_method}
          onChange={(e) =>
            updateDraft(record.key, "shipping_method", e.target.value)
          }
        />
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (_, record) => (
        <TextArea
          rows={2}
          value={record.notes}
          onChange={(e) => updateDraft(record.key, "notes", e.target.value)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="Pre-filled return entry from quarantined materials">
              <h3>
                Return & Replacement Module – Return Entry (Pre-filled from
                Quarantine)
              </h3>
            </Tooltip>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/inventory-quarantined-material-queue">
                  Quarantined Material Queue
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Return Entry (Quarantine)</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>
      <section className="grid-sec FormSection Container aidocintak">
        <Spin spinning={loading} tip="Loading...">
          <div className="FormBody">
            <div className="stepscontentwrap" style={{ padding: 0 }}>
              <div className="stepsContent" style={{ paddingRight: "5px" }}>
                <div className="po-good-header">
                  <div className="po-header-details" style={{ margin: "0" }}>
                    <div className="po-row">
                      <strong>Location :</strong>
                      <span>
                        {context?.site_name ? `${context.site_name}` : "—"}
                      </span>
                    </div>

                    <div className="po-row">
                      <strong>Date :</strong>
                      <span>{context?.confirmation_date ?? "—"}</span>
                    </div>
                    <div className="po-row">
                      <strong>Source Ref :</strong>
                      <span>{context.sourceRef}</span>
                    </div>
                  </div>
                </div>
                <Divider
                  orientation="left"
                  plain
                  orientationMargin={0}
                  style={{ marginBottom: 0 }}
                >
                  <div className="returninfo-title">
                    <span className="count">1</span> Review Item for Return
                    (Pre-filled)
                  </div>
                </Divider>

                <div
                  className="tablewrp"
                  style={{
                    paddingLeft: "34px",
                    marginTop: "10px",
                    marginBottom: "24px",
                  }}
                >
                  <Table
                    rowKey="key"
                    columns={infoColumns}
                    dataSource={infoRows}
                    pagination={false}
                    size="small"
                    scroll={{ x: 900 }}
                  />
                </div>
                <Divider
                  orientation="left"
                  plain
                  orientationMargin={0}
                  style={{ marginBottom: 0 }}
                >
                  <div className="returninfo-title">
                    <span className="count">1</span> Complete Return Request
                    Draft (Destination Context)
                  </div>
                </Divider>

                <div
                  className="tablewrp"
                  style={{
                    paddingLeft: "34px",
                    marginTop: "10px",
                    marginBottom: "16px",
                  }}
                >
                  <Table
                    rowKey="key"
                    columns={draftColumns}
                    dataSource={draft}
                    pagination={false}
                    size="small"
                    scroll={{ x: 1000 }}
                    locale={{ emptyText: "No items in draft." }}
                  />
                </div>
              </div>
              <div className="FormFooter">
                <div className="good-tge">
                  <Alert
                    type="info"
                    showIcon
                    style={{
                      width: "100%",
                      padding: "5px 10px",
                      borderRadius: 4,
                    }}
                    message={
                      <span>
                        System Action: On submission, generates RA ID and
                        informs Central Procurement for logistics planning. Item
                        remains in Quarantine until replacement or closure.
                      </span>
                    }
                  />
                </div>
                <Space style={{ justifyContent: "flex-end" }}>
                  <Button
                    type="primary"
                    className="SaveGradiantBtn"
                    disabled={disableSubmit}
                    loading={submitLoading}
                    onClick={confirmSubmitReturnRequest}
                  >
                    Submit Return Request (Generate RA ID)
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        </Spin>
      </section>
    </>
  );
};

export default ReturnQuarantinedMaterial;
